import { useState, useEffect } from "react";

import { useQuery } from "@apollo/react-hooks";
import useEnumState from "../../../hooks/useEnumState";
import { camelize } from "../../../helpers/StringManipulation";
import { isPriceValid, getMinimumPrice } from "../helpers";
import { ziplineFee } from "../../OpportunitySidebar/helpers";
import { getProduct, getCategories, getAccountFee } from "../queries";
import useCreateProduct from "./useCreateProduct";
import useEditProduct from "./useEditProduct";
import useTutorialsState from "../../../hooks/useTutorialsState";
import useProductAlerts from "./useProductAlerts";
import useTagAccount from "../../../hooks/useTagAccount";
import { useMixpanel } from "react-mixpanel-browser";

const INFO_TAB = "Information";
const VARIANTS_TAB_FULL = "Product Variants";
const VARIANTS_TAB = "ProductVariants";
const IMAGES_TAB = "Images";

export const useGetProductAndCategories = (productId) => {
  const { data: productData, loading: productLoading } = useQuery(getProduct, {
    variables: { id: productId },
    skip: !productId
  });

  const { data: categoriesData, loading: categoriesLoading } = useQuery(
    getCategories
  );

  return {
    product: productData && productData.products && productData.products[0],
    categories: categoriesData && categoriesData.categories,
    loadingProductOrCategories: productLoading || categoriesLoading
  };
};

export const useChangeProduct = ({
  sidebarIsOpen,
  product,
  productId,
  searchText,
  onDismiss,
  setError,
  productSidebarInfoProps: informationTabProps,
  productSidebarVariantsProps: variantTabProps,
  productSidebarImagesProps: imageTabProps
}) => {
  const initialState = {
    activeTab: INFO_TAB,
    useEditMutation: !!productId,
    saveIsLoading: "",
    isValidatingForms: false,
    disableSave: false
  };

  const {
    hasVariants,
    formHasAnInvalidField: infoFormHasAnInvalidField
  } = informationTabProps;

  const {
    formHasAnInvalidField: variantFormHasAnInvalidField
  } = variantTabProps;

  const tabTitles = hasVariants
    ? [INFO_TAB, VARIANTS_TAB_FULL, IMAGES_TAB]
    : [INFO_TAB, IMAGES_TAB];

  const mixpanel = useMixpanel();

  const [activeTab, setActiveTab] = useEnumState(
    tabTitles.map(camelize),
    initialState.activeTab
  );

  const [useEditMutation, setUseEditMutation] = useState(
    initialState.useEditMutation
  );
  const [saveIsLoading, setSaveIsLoading] = useState(
    initialState.saveIsLoading
  );

  const [createProduct] = useCreateProduct({
    product,
    searchText,
    informationTabProps,
    variantTabProps,
    imageTabProps
  });

  const [editProduct] = useEditProduct({
    productId,
    product,
    searchText,
    informationTabProps,
    variantTabProps,
    imageTabProps
  });

  const [isValidatingForms, setIsValidatingForms] = useState(
    initialState.isValidatingForms
  );

  const { getHasCompletedTutorial, completeTutorial } = useTutorialsState();
  const { tagAccount } = useTagAccount();

  const onSave = async () => {
    if (
      infoFormHasAnInvalidField ||
      (hasVariants && variantFormHasAnInvalidField)
    ) {
      if (hasVariants) {
        if (
          activeTab === IMAGES_TAB &&
          (infoFormHasAnInvalidField || variantFormHasAnInvalidField)
        )
          setActiveTab(
            !infoFormHasAnInvalidField && variantFormHasAnInvalidField
              ? VARIANTS_TAB
              : INFO_TAB
          );
        else if (
          activeTab === INFO_TAB &&
          !infoFormHasAnInvalidField &&
          variantFormHasAnInvalidField
        )
          setActiveTab(VARIANTS_TAB);
        else if (
          activeTab === VARIANTS_TAB &&
          !variantFormHasAnInvalidField &&
          infoFormHasAnInvalidField
        )
          setActiveTab(INFO_TAB);
      } else if (activeTab === IMAGES_TAB && infoFormHasAnInvalidField)
        setActiveTab(INFO_TAB);

      return setIsValidatingForms(true);
    }

    setIsValidatingForms(false);
    setSaveIsLoading(true);
    try {
      const productMutation = useEditMutation ? editProduct : createProduct;

      const {
        data: {
          [useEditMutation ? "editProduct" : "createProduct"]: {
            id: ProductId,
            name: ProductName,
            description: Description,
            price: Price,
            sku: Sku,
            stock: Stock
          }
        }
      } = await productMutation();

      if (!useEditMutation) {
        const hasAddedProduct = getHasCompletedTutorial("Add Product");

        if (!hasAddedProduct) {
          await completeTutorial({
            variables: { name: "Add Product", version: 1 }
          });
          await tagAccount("Product Created");
        }
      }

      mixpanel.track(`Product ${useEditMutation ? "Edited" : "Created"}`, {
        ProductId,
        ProductName,
        Description,
        Price,
        Sku,
        Stock
      });

      setUseEditMutation(true);
      setSaveIsLoading(false);
      onDismissSidebar(true);
    } catch (err) {
      setError("Cannot edit price on a product with an active opportunity");
      setTimeout(() => setError(null), 8000);
      setSaveIsLoading(false);
      console.error(err);
    }
  };

  const [disableSave, setDisableSave] = useState(initialState.disableSave);

  const resetState = () => {
    setActiveTab(initialState.activeTab);
    setUseEditMutation(initialState.useEditMutation);
    setSaveIsLoading(initialState.saveIsLoading);
    setIsValidatingForms(initialState.isValidatingForms);
    setDisableSave(initialState.disableSave);
  };

  const {
    onDismissSidebar,
    alertConfirmButtonText,
    isAlertOpen,
    onAlertCancel,
    onAlertConfirm,
    alertMessage,
    deleteVariant,
    deleteVariantOption,
    onRemoveImage
  } = useProductAlerts({
    setActiveTab,
    INFO_TAB,
    resetState,
    informationTabProps,
    variantTabProps,
    imageTabProps,
    onDismiss
  });

  useEffect(() => {
    setDisableSave(
      isValidatingForms &&
        ((activeTab === INFO_TAB && infoFormHasAnInvalidField) ||
          (activeTab === VARIANTS_TAB && variantFormHasAnInvalidField))
    );
  }, [
    isValidatingForms,
    activeTab,
    infoFormHasAnInvalidField,
    variantFormHasAnInvalidField
  ]);

  useEffect(() => {
    setUseEditMutation(!!productId);
  }, [productId]);

  useEffect(() => {
    if (sidebarIsOpen) {
      window.onbeforeunload = (e) => {
        e.preventDefault();
        return true;
      };
    } else {
      window.onbeforeunload = () => {};
    }
  }, [sidebarIsOpen]);

  return {
    tabTitles,
    activeTab,
    setActiveTab,
    onSave,
    isValidatingForms,
    pageIsLoading:
      saveIsLoading ||
      informationTabProps.isImageLoading ||
      imageTabProps.imagesAreLoading,
    disableSave,
    onDismissSidebar,
    alertConfirmButtonText,
    isAlertOpen,
    onAlertCancel,
    onAlertConfirm,
    alertMessage,
    deleteVariant,
    deleteVariantOption,
    onRemoveImage
  };
};

export const useGetAccountFee = (price, acceptsDonations) => {
  const { data, loading } = useQuery(getAccountFee);

  return {
    fixedFee: !loading && data.accounts[0].fixedFee,
    variableFee: !loading && data.accounts[0].variableFee,
    ziplineFee:
      !loading &&
      !!price &&
      ziplineFee(
        price,
        data.accounts[0].fixedFee,
        data.accounts[0].variableFee
      ),
    isPriceValid:
      (!loading &&
        isPriceValid(
          price,
          data.accounts[0].fixedFee,
          data.accounts[0].variableFee
        )) ||
      acceptsDonations,
    minimumPrice:
      !loading &&
      getMinimumPrice(data.accounts[0].fixedFee, data.accounts[0].variableFee)
  };
};
